import {
  ApartmentOutlined, ArrowLeftOutlined, ArrowRightOutlined,
  BankOutlined, CheckCircleFilled, ClusterOutlined, DeleteOutlined, DeploymentUnitOutlined, DownOutlined,
  DownloadOutlined, EditOutlined, FileSearchOutlined, FilterOutlined, IdcardOutlined,
  InboxOutlined, PlusOutlined, ProfileOutlined,
  SearchOutlined, TeamOutlined, UploadOutlined,
} from '@ant-design/icons';
import {
  Button, Card, Drawer, Empty, Form, Input, InputNumber, Popconfirm,
  Select, Space, Steps, Table, Tag, Tooltip, Typography, Upload, message,
} from 'antd';
import { useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import useTopbarTitleHooks from 'hooks/useTopbarTitleHooks';
import { APP_ROUTE_PREFIX, RouteConstant } from 'routes/routesConstant';
import { getSelectedTenant, saveSelectedTenant } from 'services/tenant/tenantMembershipService';

const DEFAULT_LEVEL_NAMES = {
  2: ['Division', 'Position'],
  3: ['Division', 'Department', 'Position'],
  4: ['Division', 'Department', 'Section', 'Position'],
  5: ['Division', 'Department', 'Section', 'Team', 'Position'],
};
const emptyDraft = { organization: { levelCount: 3 }, levelNames: DEFAULT_LEVEL_NAMES[3], levelData: [[], [], []] };
const draftStorageKey = (id) => `workplansiq.organizationSetupDraft.${id || 'unknown'}`;
const readSessionJson = (key, fallback = null) => {
  try { return JSON.parse(sessionStorage.getItem(key) || 'null') || fallback; }
  catch { return fallback; }
};
const readDraft = (id) => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(draftStorageKey(id)) || '{}');
    const legacyOrganization = readSessionJson('workplansiq.workspaceSetupOrganizationDraft', {});
    const organization = {
      ...emptyDraft.organization,
      ...(legacyOrganization?.dataField || legacyOrganization || {}),
      ...saved.organization,
    };
    if (!organization.name && organization.orgName) organization.name = organization.orgName;
    if (!organization.email && organization.orgEmail) organization.email = organization.orgEmail;

    const levelNames = saved.levelNames || DEFAULT_LEVEL_NAMES[Number(organization.levelCount) || 3];
    const currentDataHasRows = saved.levelData?.some((rows) => rows?.length);
    let levelData = currentDataHasRows ? saved.levelData : [
      saved.division || readSessionJson('workplansiq.workspaceSetup.division', []),
      saved.department || readSessionJson('workplansiq.workspaceSetup.department', []),
      saved.position || readSessionJson('workplansiq.workspaceSetup.position', []),
    ];

    levelData = Array.from({ length: levelNames.length }, (_, levelIndex) => {
      const rows = levelData[levelIndex] || [];
      return rows.map((row, rowIndex) => ({
        ...row,
        id: row.id || `migrated-level-${levelIndex}-${rowIndex}`,
        ...(levelIndex > 0 && !row.parentId && levelData[levelIndex - 1]?.length
          ? { parentId: levelData[levelIndex - 1][0].id || `migrated-level-${levelIndex - 1}-0` }
          : {}),
      }));
    });

    const migrated = { ...emptyDraft, ...saved, organization, levelNames, levelData };
    sessionStorage.setItem(draftStorageKey(id), JSON.stringify(migrated));
    return migrated;
  } catch { return emptyDraft; }
};
const pluralize = (name) => name?.endsWith('s') ? name : `${name}s`;
const getLevelIcon = (name = '') => {
  const normalized = name.toLowerCase();
  if (normalized.includes('division') || normalized.includes('directorate')) return <ApartmentOutlined />;
  if (normalized.includes('department') || normalized.includes('section') || normalized.includes('unit')) return <ClusterOutlined />;
  if (normalized.includes('team') || normalized.includes('group')) return <TeamOutlined />;
  if (normalized.includes('position') || normalized.includes('role') || normalized.includes('job')) return <IdcardOutlined />;
  return <ApartmentOutlined />;
};

export default function WorkspaceSetupPage() {
  useTopbarTitleHooks('Organization Setup');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const organization = getSelectedTenant();
  const [draft, setDraft] = useState(() => readDraft(organization?.id));
  const maxStep = draft.levelNames.length + 3;
  const requestedStep = Number(searchParams.get('step'));
  const [current, setCurrent] = useState(Number.isInteger(requestedStep) ? Math.min(Math.max(requestedStep, 0), maxStep) : 0);
  const [entityLevel, setEntityLevel] = useState(null);
  const [editingEntity, setEditingEntity] = useState(null);
  const [entitySearch, setEntitySearch] = useState('');
  const [expandedLevelIndex, setExpandedLevelIndex] = useState(0);
  const [logoFile, setLogoFile] = useState(null);
  const [levelCount, setLevelCount] = useState(draft.levelNames.length);
  const [organizationForm] = Form.useForm();
  const [levelForm] = Form.useForm();
  const [entityForm] = Form.useForm();
  const watchedLevelNames = Form.useWatch('names', levelForm);
  const previewLevelNames = watchedLevelNames?.length ? watchedLevelNames : draft.levelNames;

  const reviewStep = draft.levelNames.length + 2;
  const completeStep = draft.levelNames.length + 3;
  const wizardSteps = [
    { title: 'Organization Information', icon: <ProfileOutlined /> },
    { title: 'Structure Levels', icon: <DeploymentUnitOutlined /> },
    ...draft.levelNames.map((name) => ({ title: pluralize(name), icon: getLevelIcon(name) })),
    { title: 'Review', icon: <FileSearchOutlined /> },
    { title: 'Complete', icon: <CheckCircleFilled /> },
  ];

  const persist = (next) => {
    setDraft(next);
    sessionStorage.setItem(draftStorageKey(organization?.id), JSON.stringify(next));
  };
  const saveProgress = (wizardStep, status = 'IN_PROGRESS') => {
    const progress = wizardStep === 0 ? 0 : wizardStep === 1 ? 1 : wizardStep < reviewStep ? 2 : wizardStep === reviewStep ? 3 : 5;
    saveSelectedTenant({ ...organization, workspaceSetupFlowVersion: 2, workspaceSetupStep: progress, workspaceSetupStatus: status });
  };
  const goTo = (step) => {
    setCurrent(step); setEntitySearch(''); setSearchParams({ step: String(step) }); saveProgress(step);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const saveOrganization = () => {
    const values = organizationForm.getFieldsValue();
    organizationForm.setFields([{ name: 'name', errors: [] }, { name: 'email', errors: [] }]);
    persist({ ...draft, organization: { ...values, levelCount: draft.levelNames.length, logoName: logoFile?.name || draft.organization.logoName || null } });
    goTo(1);
  };
  const saveLevels = () => {
    const values = levelForm.getFieldsValue();
    const names = Array.from({ length: levelCount }, (_, index) => values.names?.[index]?.trim() || `Level ${index + 1}`);
    const countChanged = levelCount !== draft.levelNames.length;
    persist({
      ...draft,
      organization: { ...draft.organization, levelCount },
      levelNames: names,
      levelData: countChanged ? Array.from({ length: levelCount }, () => []) : draft.levelData,
    });
    goTo(2);
  };
  const changeLevelCount = (count) => {
    setLevelCount(count);
    levelForm.setFieldsValue({ names: DEFAULT_LEVEL_NAMES[count] });
  };
  const selectLogo = (file) => {
    if (!['image/jpeg', 'image/png', 'image/svg+xml'].includes(file.type)) { message.error('Logo must use JPG, PNG, or SVG format.'); return Upload.LIST_IGNORE; }
    if (file.size > 2 * 1024 * 1024) { message.error('Logo size must not exceed 2 MB.'); return Upload.LIST_IGNORE; }
    setLogoFile(file); return false;
  };
  const addEntity = async () => {
    const values = await entityForm.validateFields();
    const nextData = draft.levelData.map((rows, index) => {
      if (index !== entityLevel) return rows;
      if (editingEntity) return rows.map((row) => row.id === editingEntity.id ? { ...row, ...values } : row);
      return [...rows, { ...values, id: `level-${index}-${Date.now()}` }];
    });
    persist({ ...draft, levelData: nextData }); entityForm.resetFields(); setEntityLevel(null); setEditingEntity(null);
  };
  const openEntityEditor = (levelIndex, row = null) => {
    setEntityLevel(levelIndex); setEditingEntity(row);
    entityForm.setFieldsValue(row || { resources: 1 });
  };
  const closeEntityEditor = () => { setEntityLevel(null); setEditingEntity(null); entityForm.resetFields(); };
  const removeEntity = (levelIndex, id) => {
    const removedIds = new Set([id]);
    for (let index = levelIndex + 1; index < draft.levelData.length; index += 1) {
      draft.levelData[index].forEach((row) => {
        if (removedIds.has(row.parentId)) removedIds.add(row.id);
      });
    }
    persist({ ...draft, levelData: draft.levelData.map((rows) => rows.filter((row) => !removedIds.has(row.id))) });
  };
  const parentName = (levelIndex, id) => draft.levelData[levelIndex - 1]?.find((row) => row.id === id)?.name || '—';
  const loadSampleHierarchy = () => {
    const sampleData = [];
    sampleData[0] = [0, 1].map((index) => ({ id: `sample-0-${index}`, name: `${draft.levelNames[0]} ${index + 1}`, code: `L1-${String(index + 1).padStart(2, '0')}`, description: `Sample ${draft.levelNames[0].toLowerCase()}` }));
    for (let levelIndex = 1; levelIndex < draft.levelNames.length; levelIndex += 1) {
      sampleData[levelIndex] = sampleData[levelIndex - 1].flatMap((parent, parentIndex) => {
        const childCount = parentIndex === 0 ? 2 : 1;
        return Array.from({ length: childCount }, (_, childIndex) => ({
          id: `sample-${levelIndex}-${parentIndex}-${childIndex}`,
          parentId: parent.id,
          name: `${draft.levelNames[levelIndex]} ${parentIndex + 1}.${childIndex + 1}`,
          code: `L${levelIndex + 1}-${parentIndex + 1}${childIndex + 1}`,
          description: `Sample ${draft.levelNames[levelIndex].toLowerCase()} under ${parent.name}`,
          ...(levelIndex === draft.levelNames.length - 1 ? { resources: 5 + childIndex } : {}),
        }));
      });
    }
    persist({ ...draft, levelData: sampleData });
    message.success('Sample parent-child hierarchy loaded.');
  };
  const finish = () => {
    saveSelectedTenant({ ...organization, name: draft.organization.name || organization.name, workspaceSetupFlowVersion: 2, workspaceSetupStep: 5, workspaceSetupStatus: 'COMPLETE', workspaceSetupCompletedAt: new Date().toISOString() });
    setCurrent(completeStep); setSearchParams({ step: String(completeStep) });
  };

  const activeLevelIndex = current >= 2 && current < reviewStep ? current - 2 : null;
  const columns = useMemo(() => {
    if (activeLevelIndex === null) return [];
    const result = [
      { title: 'No', width: 58, render: (_, __, index) => index + 1 },
      { title: `${draft.levelNames[activeLevelIndex]} Name`, dataIndex: 'name' },
      { title: 'Code', dataIndex: 'code', width: 130 },
      { title: 'Description', dataIndex: 'description', ellipsis: true, render: (value) => value || '—' },
    ];
    if (activeLevelIndex > 0) result.push({ title: `Parent ${draft.levelNames[activeLevelIndex - 1]}`, dataIndex: 'parentId', render: (id) => parentName(activeLevelIndex, id) });
    if (activeLevelIndex < draft.levelNames.length - 1) result.push({ title: `Number of ${pluralize(draft.levelNames[activeLevelIndex + 1])}`, width: 140, render: (_, row) => draft.levelData[activeLevelIndex + 1]?.filter((child) => child.parentId === row.id).length || 0 });
    else result.push({ title: 'Resources', dataIndex: 'resources', width: 100 });
    result.push({ title: 'Action', width: 110, render: (_, row) => <Space size={8}><Tooltip title="Edit"><Button className="org-table-action org-table-action--edit" type="text" icon={<EditOutlined />} aria-label="Edit" onClick={() => openEntityEditor(activeLevelIndex, row)} /></Tooltip><Popconfirm title={`Delete this ${draft.levelNames[activeLevelIndex].toLowerCase()}?`} okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => removeEntity(activeLevelIndex, row.id)}><Tooltip title="Delete"><Button className="org-table-action org-table-action--delete" type="text" icon={<DeleteOutlined />} aria-label="Delete" /></Tooltip></Popconfirm></Space> });
    return result;
  }, [activeLevelIndex, draft]);

  const filteredLevelRows = activeLevelIndex === null ? [] : (draft.levelData[activeLevelIndex] || []).filter((row) => {
    const query = entitySearch.trim().toLowerCase();
    return !query || [row.name, row.code, row.description].some((value) => String(value || '').toLowerCase().includes(query));
  });

  if (!organization) return <Navigate to={`${APP_ROUTE_PREFIX}/${RouteConstant.dashboard}`} replace />;

  return <div className="org-wizard-page">
    <aside className="org-wizard-sidebar">
      <div className="org-wizard-brand"><BankOutlined /><span><strong>WorkPlansIQ</strong><small>Organization Setup</small></span></div>
      <Steps direction="vertical" current={current} items={wizardSteps} />
      <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`${APP_ROUTE_PREFIX}/${RouteConstant.dashboard}`)}>Save and exit</Button>
    </aside>
    <main className="org-wizard-main">
      <div className="org-wizard-topbar"><span>Step {current + 1} of {wizardSteps.length}</span><Tag color="blue">Saved in this browser</Tag></div>

      {current === 0 ? <Card className="org-wizard-content-card">
        <WizardHeading eyebrow="ORGANIZATION PROFILE" title="Organization Information" description="Enter the basic identity and contact information for this organization." />
        <div className="org-profile-layout">
          <Form form={organizationForm} layout="vertical" initialValues={draft.organization} className="org-wizard-form">
            <Form.Item name="name" label="Organization Name" rules={[{ required: true }]}><Input placeholder="e.g. PT Nusantara Sejahtera" /></Form.Item>
            <Form.Item name="shortName" label="Short Name (Optional)"><Input placeholder="e.g. NUS" maxLength={20} /></Form.Item>
            <Form.Item name="email" label="Organization Email" rules={[{ type: 'email' }]}><Input placeholder="admin@company.com" /></Form.Item>
            <Form.Item name="phone" label="Phone Number"><Input placeholder="e.g. +62 21 1234 5678" /></Form.Item>
            <Form.Item name="address" label="Address" className="org-wizard-form__wide"><Input.TextArea rows={4} maxLength={500} showCount placeholder="Organization address" /></Form.Item>
          </Form>
          <section className="org-logo-field"><strong>Organization Logo <span>(Optional)</span></strong><Upload.Dragger accept=".jpg,.jpeg,.png,.svg" beforeUpload={selectLogo} maxCount={1} fileList={logoFile ? [logoFile] : []} onRemove={() => setLogoFile(null)}><p className="ant-upload-drag-icon"><InboxOutlined /></p><p className="ant-upload-text">Drag and drop your logo here</p><p className="ant-upload-hint">or click to select a file</p><Button type="primary" size="small">Choose File</Button><small>JPG, PNG, or SVG · Maximum 2 MB</small></Upload.Dragger></section>
        </div>
        <WizardActions back={() => navigate(`${APP_ROUTE_PREFIX}/${RouteConstant.dashboard}`)} next={saveOrganization} nextLabel="Continue to Structure Levels" />
      </Card> : null}

      {current === 1 ? <Card className="org-wizard-content-card">
        <WizardHeading eyebrow="STRUCTURE LEVELS" title="Define Organization Levels" description="Choose the number of levels, then give each level a name. These names will become the next data-entry steps." />
        <div className="dynamic-level-layout">
          <div className="dynamic-level-configuration">
            <label><span>Number of Organization Levels</span><Select className="organization-level-count-select" suffixIcon={<DownOutlined />} value={levelCount} onChange={changeLevelCount} options={[2, 3, 4, 5].map((value) => ({ value, label: `${value} Levels` }))} /></label>
            <Form form={levelForm} layout="vertical" initialValues={{ names: draft.levelNames }} className="dynamic-level-form">
              <Form.List name="names">{(fields) => fields.map((field, index) => <Form.Item {...field} key={field.key} label={`Level ${index + 1}`} rules={[{ required: true, message: 'Level name is required' }]}><Input maxLength={30} showCount placeholder="e.g. Division, Department, Position" /></Form.Item>)}</Form.List>
            </Form>
          </div>
          <aside className="dynamic-level-preview"><strong>Organization Structure Preview</strong>{previewLevelNames.map((name, index) => <div key={`${index}-${name}`}><span className="dynamic-level-name">{name || `Level ${index + 1}`}</span>{index < previewLevelNames.length - 1 ? <span className="dynamic-level-connector" aria-hidden="true">↓</span> : null}</div>)}</aside>
        </div>
        <WizardActions back={() => goTo(0)} next={saveLevels} nextLabel={`Continue to ${pluralize(draft.levelNames[0])}`} />
      </Card> : null}

      {activeLevelIndex !== null ? <Card className="org-wizard-content-card">
        <div className="org-wizard-heading org-wizard-heading--data"><div><Typography.Text className="onboarding-eyebrow">LEVEL {activeLevelIndex + 1} DATA</Typography.Text><Typography.Title level={3}>{pluralize(draft.levelNames[activeLevelIndex])}</Typography.Title><Typography.Paragraph>Add data for this level and connect it to its parent level.</Typography.Paragraph></div><Space wrap><Button icon={<DownloadOutlined />} onClick={() => message.info('Template download will be connected to the import API.')}>Download Template</Button><Button icon={<UploadOutlined />} onClick={() => message.info('Bulk upload preview will be connected to the import API.')}>Bulk Upload</Button><Button type="primary" icon={<PlusOutlined />} onClick={() => openEntityEditor(activeLevelIndex)}>Add {draft.levelNames[activeLevelIndex]}</Button></Space></div>
        <div className="org-level-toolbar"><Input allowClear prefix={<SearchOutlined />} value={entitySearch} onChange={(event) => setEntitySearch(event.target.value)} placeholder={`Search ${draft.levelNames[activeLevelIndex].toLowerCase()} name or code...`} /><Button icon={<FilterOutlined />}>Filter</Button></div>
        {draft.levelData[activeLevelIndex]?.length ? <Table rowKey="id" columns={columns} dataSource={filteredLevelRows} scroll={{ x: 900 }} pagination={{ pageSize: 5, showSizeChanger: false, showTotal: (total, range) => `Showing ${range[0]}–${range[1]} of ${total} records` }} /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={`No ${pluralize(draft.levelNames[activeLevelIndex]).toLowerCase()} added`}><Button type="primary" onClick={() => openEntityEditor(activeLevelIndex)}>Add {draft.levelNames[activeLevelIndex]}</Button></Empty>}
        <WizardActions back={() => goTo(current - 1)} next={() => goTo(current + 1)} nextLabel={activeLevelIndex === draft.levelNames.length - 1 ? 'Review Structure' : `Continue to ${pluralize(draft.levelNames[activeLevelIndex + 1])}`} />
      </Card> : null}

      {current === reviewStep ? <Card className="org-wizard-content-card">
        <WizardHeading eyebrow="REVIEW & CONFIRM" title="Organization Structure Summary" description="Review every configured level before completing setup." />
        <div className="org-review-summary"><div><BankOutlined /><span><strong>{draft.organization.name}</strong><small>{draft.organization.email || 'No organization email'}</small></span><Tag color="green">Ready</Tag></div></div>
        <section className="org-structure-by-level">
          <div className="org-level-summary-list">
            {draft.levelNames.map((name, index) => <div className={`org-level-section${expandedLevelIndex === index ? ' is-expanded' : ''}`} key={`${name}-${index}`}>
              <div className="org-level-summary-row">
                <button type="button" aria-label={`${expandedLevelIndex === index ? 'Hide' : 'Show'} ${pluralize(name)}`} onClick={() => setExpandedLevelIndex((value) => value === index ? null : index)}><DownOutlined className={expandedLevelIndex === index ? 'is-expanded' : ''} /></button>
                <span><strong>Level {index + 1} – {pluralize(name)} ({draft.levelData[index]?.length || 0})</strong><small>{index === 0 ? 'Root organization level' : `Child of ${draft.levelNames[index - 1]}`}</small></span>
                <Tooltip title={`Edit ${pluralize(name)}`}><Button className="org-table-action org-table-action--edit" type="text" icon={<EditOutlined />} aria-label={`Edit ${pluralize(name)}`} onClick={() => goTo(index + 2)} /></Tooltip>
              </div>
              {expandedLevelIndex === index ? <div className="org-level-child-list">
                {(draft.levelData[index] || []).length ? draft.levelData[index].map((row) => <div className="org-level-child-row" key={row.id}>
                  <span className="org-level-child-row__icon">{getLevelIcon(name)}</span>
                  <span><strong>{row.name}</strong><small>{row.code}{index > 0 ? ` · Parent: ${parentName(index, row.parentId)}` : ''}</small></span>
                  {row.resources ? <Tag color="blue">{row.resources} resources</Tag> : null}
                </div>) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={`No ${pluralize(name).toLowerCase()} available`}><Space wrap><Button onClick={() => goTo(index + 2)}>Add {name}</Button>{index === 0 ? <Button type="primary" onClick={loadSampleHierarchy}>Load Sample Hierarchy</Button> : null}</Space></Empty>}
              </div> : null}
            </div>)}
          </div>
        </section>
        <WizardActions back={() => goTo(reviewStep - 1)} next={finish} nextLabel="Save and Complete Setup" />
      </Card> : null}

      {current === completeStep ? <Card className="org-wizard-content-card org-wizard-complete-card"><section className="org-setup-complete">
        <div className="org-setup-complete__illustration"><span><BankOutlined /></span><CheckCircleFilled /></div>
        <Typography.Title level={2}>Organization Setup Complete!</Typography.Title>
        <Typography.Paragraph>The organization structure is ready to use. You can manage members, update organization data, or continue to the Dashboard.</Typography.Paragraph>
        <div className="org-setup-complete__actions">
          <Button type="primary" size="large" onClick={() => navigate(`${APP_ROUTE_PREFIX}/${RouteConstant.dashboard}`)}>Go to Dashboard</Button>
          <Button size="large" onClick={() => navigate(`${APP_ROUTE_PREFIX}/${RouteConstant.tenantSelection}?mode=manage`)}>Manage Organizations</Button>
        </div>
      </section></Card> : null}
    </main>

    <Drawer title={entityLevel !== null ? `${editingEntity ? 'Edit' : 'Add'} ${draft.levelNames[entityLevel]}` : ''} width={400} open={entityLevel !== null} onClose={closeEntityEditor} footer={<div className="org-entity-drawer-footer"><Button onClick={closeEntityEditor}>Cancel</Button><Button type="primary" onClick={addEntity}>Save</Button></div>}>
      <Form form={entityForm} layout="vertical"><Form.Item name="name" label={`${entityLevel !== null ? draft.levelNames[entityLevel] : ''} Name`} rules={[{ required: true }]}><Input placeholder="Enter name" /></Form.Item><Form.Item name="code" label={`${entityLevel !== null ? draft.levelNames[entityLevel] : ''} Code`} rules={[{ required: true }]}><Input placeholder="e.g. DIV-IT" /></Form.Item>{entityLevel > 0 ? <Form.Item name="parentId" label={`Parent ${draft.levelNames[entityLevel - 1]}`} rules={[{ required: true }]}><Select placeholder={`Select ${draft.levelNames[entityLevel - 1].toLowerCase()}`} options={(draft.levelData[entityLevel - 1] || []).map((row) => ({ value: row.id, label: row.name }))} /></Form.Item> : null}{entityLevel === draft.levelNames.length - 1 ? <Form.Item name="resources" label="Number of Resources" initialValue={1}><InputNumber min={1} style={{ width: '100%' }} /></Form.Item> : null}<Form.Item name="description" label="Description (Optional)"><Input.TextArea rows={5} maxLength={200} showCount placeholder="Enter description" /></Form.Item></Form>
    </Drawer>
  </div>;
}

function WizardHeading({ eyebrow, title, description }) {
  return <div className="org-wizard-heading"><div><Typography.Text className="onboarding-eyebrow">{eyebrow}</Typography.Text><Typography.Title level={3}>{title}</Typography.Title><Typography.Paragraph>{description}</Typography.Paragraph></div></div>;
}
function WizardActions({ back, next, nextLabel }) {
  return <div className="org-wizard-actions"><Button icon={<ArrowLeftOutlined />} onClick={back}>Back</Button><Space><Button onClick={() => message.success('Draft saved in this browser.')}>Save Draft</Button><Button type="primary" onClick={next}>{nextLabel} <ArrowRightOutlined /></Button></Space></div>;
}
